import {
	CAMPAIGN_STATUS,
	CAMPAIGN_TYPE,
	MESSAGE_DEFINITION_INTERVAL,
	MESSAGE_DEFINITION_STATUS,
	MESSAGE_DEFINITION_TYPE
} from './const';
import {v4} from 'uuid';

/**
 * Data object representing the campaign list item
 * @note This is usually used in data table as a row. Params don't have default values since this data object is used to
 * represent data that actually exists.
 */
export class CampaignListItemDataObject {
	/**
	 * @param {CampaignType} type - Campaign type.
	 * @param {string} id - Campaign's DB ID.
	 * @param {string} name - Campaign's name.
	 * @param {string} description - Campaign's description.
	 * @param {CampaignStatus} status - Campaign's status.
	 * @param {Date} [startDate=null] - Campaign's stating date.
	 * @param {Date} [endDate=null] - Campaign's end date.
	 */
	constructor(type, id, name, description, status, startDate = null, endDate = null) {
		this.type = type;
		this.id = id;
		this.name = name;
		this.description = description;
		this.status = status;
		this.startDate = startDate;
		this.endDate = endDate;
	}
}

/**
 * Data object representing the campaign item
 * @note This is usually used when creating or updating data table item using a popup or a dialog. Params have default 
 * values since this data object can be used for creating an item. This is why 'id' has a default value of null which 
 * represents a new item that is being created.
 */
export class CampaignItemDataObject {
	/**
	 * @param {CampaignType} [type=CAMPAIGN_TYPE.REGULAR] - Campaign type.
	 * @param {string|null} [id=null] - Campaign's DB ID.
	 * @param {string} projectId - Project's DB ID.
	 * @param {string} [name=''] - Campaign's name.
	 * @param {string} [description=''] - Campaign's description.
	 * @param {Date} [startDate=null] - Campaign's start date.
	 * @param {Date} [endDate=null] - Campaign's end date.
	 * @param {CampaignStatus} status - Campaign's status.
	 * @param {?{label: string, value: string, active: boolean}} [cappingPlan=null] - Capping plan for this campaign.
	 * @param {string|null} [clientId=null] - Client's DB ID used when sysadmin is working with the app as a client. This 
	 * property is set by the app (sysadmin selects a client to work with) and not loaded from IO (write-only).
	 */
	constructor(
		type = CAMPAIGN_TYPE.REGULAR, id = null, projectId, name = '', description = '', startDate = null, endDate = null, 
		status = CAMPAIGN_STATUS.IN_PREPARATION, cappingPlan = null, clientId = null,
	) {
		this.type = type;
		this.id = id;
		this.projectId = projectId;
		this.name = name;
		this.description = description;
		this.startDate = startDate;
		this.endDate = endDate;
		this.status = status;
		this.cappingPlan = cappingPlan;
		
		// Write-only properties
		this.clientId = clientId;
	}
}



export class MessageDefinitionDataObject {
	/**
	 * @param {MessageDefinitionType} [type=MESSAGE_DEFINITION_TYPE.REGULAR] - Message definition type.
	 * @param {?string} [id=null] - Message definition DB ID.
	 * @param {string} campaignId - DB ID of the campaign that this message definition belongs to.
	 * @param {string} timeZoneId - Timezone identifier (for example: 'Europe/Belgrade').
	 * @param {boolean} [personalized=false] - Flag that specifies if message will be personalized to each recipient by
	 * using dynamic values like first and last name.
	 * @param {MessageDefinitionChannelDataObject[]} [channels=[]] - Message sending channels.
	 * @param {Date} [startDate=null] - Date from which messages created with this message definition will become active.
	 * @param {number} [messageCount=0] - Number of messages that will be sent in bulk on each interval.
	 * @param {MessageDefinitionInterval} [interval='DAY'] - Interval for sending messages (for example per hour or day).
	 * @param {MessageContentDataObject[]} [messageContents=[]] - List of all the different
	 * messages that can be sent by this message definition.
	 * @param {string} [deliveryStartTime=''] - Time of day when system will start delivering user messages.
	 * @param {string} [deliveryEndTime=''] - Time of day when system will stop delivering user messages.
	 * @param {number} [timeToLive] - Timeout (in minutes) for the delivery of messages.
	 * @param {number} [numberOfRetries] - Number of times system will try to send a customer message.
	 * @param {number} [pauseBetweenRetries] - Time (in minutes) that must elapse between retries.
	 * @param {MessageDefinitionStatus|''} [status=''] - Message definition status.
	 * @param {?number} [ordinal=null] - Message definition ordinal (read-only).
	 * @param {boolean} [messageContentEditable=true] - Flag that specifies if the user can edit message content.
	 */
	constructor(
		type = MESSAGE_DEFINITION_TYPE.REGULAR, id = null, campaignId, timeZoneId, personalized = false, channels = [], 
		startDate = null, messageCount = 8640000, interval = MESSAGE_DEFINITION_INTERVAL.DAY, messageContents = [], 
		deliveryStartTime = '', deliveryEndTime = '', timeToLive, numberOfRetries, pauseBetweenRetries, 
		status = MESSAGE_DEFINITION_STATUS.IN_PREPARATION, ordinal = null, messageContentEditable = true,
	) {
		this.type = type;
		this.id = id;
		this.campaignId = campaignId;
		this.timeZoneId = timeZoneId;
		this.personalized = personalized;
		this.channels = channels;
		this.startDate = startDate;
		this.messageCount = messageCount;
		this.interval = interval;
		this.messageContents = messageContents;
		this.deliveryStartTime = deliveryStartTime;
		this.deliveryEndTime = deliveryEndTime;
		this.timeToLive = timeToLive;
		this.numberOfRetries = numberOfRetries;
		this.pauseBetweenRetries = pauseBetweenRetries;
		this.status = status;
		this.ordinal = ordinal;
		this.messageContentEditable = messageContentEditable;
	}
}

export class MessageDefinitionChannelDataObject {
	/**
	 * @param {string|null} [id=null] - Message definition channel's DB ID (ID form the relation table).
	 * @param {string|null} [channelId=null] - Channel's DB ID.
	 * @param {string} [channelName=''] - Name of the channel.
	 * @param {CampaignMessageContentType|''} [messageType=''] - Message type used by the channel.
	 * @param {?string} [sender=null] - For SMS and Viber messages this field is set to short code. For email messages 
	 * this field is set to the email address that email is sent from. For push notifications this field can be empty.
	 * @param {string} [senderName=''] - This field is populated only for email messages, and it is set to the name of 
	 * the sender ("from name") set on the email message when sending an email.
	 * @param {string} [GUIID] - GUI ID of the message definition channel used exclusively by GUI to manage the list of 
	 * channels.
	 */
	constructor(
		id = null, channelId = null, channelName = '', messageType = '', sender = null, senderName = '', GUIID = v4(),
	) {
		this.id = id;
		this.channelId = channelId;
		this.channelName = channelName;
		this.messageType = messageType;
		this.sender = sender;
		this.senderName = senderName;
		this.GUIID = GUIID;
	}
}



export class MessageContentDataObject {
	/**
	 * @param {string|null} [id=null] - DB ID of the message content item.
	 * @param {CampaignMessageContentType|''} [messageType=''] - Message type (SMS, Viber, etc.).
	 * @param {string} [subject=''] - Subject of the message.
	 * @param {string} [body=''] - Message body text.
	 * @param {any} [bodySource] - Message body source.
	 * @param {?string} [image=null] - URL of the image that will be added below the message body.
	 * @param {boolean} [containsVideo=false] - Flag that specifies if the message body contains a video.
	 * @param {string} [video=''] - URL of the video that will be added below the message body.
	 * @param {string} [videoThumbnail=''] - URL of the video thumbnail that will be added below the message body.
	 * @param {?string} [link=null] - Link used for the button that will be added to the bottom of the message.
	 * @param {string} [linkText=''] - Label of the button that will be added to the bottom of the message.
	 * @param {?ViberCarouselItemDataObject[]} [carouselItems=null] - List of items that will be displayed in the 
	 * carousel (Viber only). Null value is used to detect if the message is not a carousel. Message will be considered 
	 * as a carousel if the value is an array even if it is empty.
	 * @param {Object} [customFields={}] - Custom fields specified by the user that will be sent to mobile app alongside
	 * the message. Custom fields are not visible in the message preview.
	 * @param {Object} [replacementFields={}] - Replacement fields specified by the user that will be used to replace 
	 * placeholders in the message content template. Replacement field values are assigned using the open API.
	 * @param {?string} [templateId=null] - ID of the message content template. If templates are used, no other param
	 * (like a subject, body, etc.) will be used since they are defined by the template. This is currently used only for
	 * WhatsApp messages since templates are created by an external tool.
	 */
	constructor(
		id = null, messageType = '', subject = '', body = '', bodySource, image = null, containsVideo = false, video = '', 
		videoThumbnail = '', link = null, linkText = '', carouselItems = null, customFields = {}, replacementFields = {}, 
		templateId = null,
	) {
		this.id = id;
		this.messageType = messageType;
		this.subject = subject;
		this.body = body;
		this.bodySource = bodySource;
		this.image = image;
		this.containsVideo = containsVideo;
		this.video = video;
		this.videoThumbnail = videoThumbnail;
		this.link = link;
		this.linkText = linkText;
		this.carouselItems = carouselItems;
		this.customFields = customFields;
		this.replacementFields = replacementFields;
		this.templateId = templateId;
	}
}


export class ChannelListItemDataObject {
	/**
	 * @param {string} value - DB ID of the channel from the channel list.
	 * @param {string} label - Name of the channel from the channel list.
	 * @param {CampaignMessageContentType} messageType - Message type used by this channel from the channel list.
	 * @param {string} mobileAppName - Name of the mobile application if channel is for push notification. 
	 * @param {ChannelListItemSenderDataObject[]} senders - List of possible senders for the channel.
	 * @param {string} providerId - DB ID of the channel provider.
	 * @param {string[]} additionalFeatures - Additional channel features.
	 */
	constructor(value, label, messageType, mobileAppName, senders, providerId, additionalFeatures) {
		this.value = value;
		this.label = label;
		this.messageType = messageType;
		this.mobileAppName = mobileAppName;
		this.senders = senders;
		this.providerId = providerId;
		this.additionalFeatures = additionalFeatures;
	}
}

export class ChannelListItemSenderDataObject {
	/**
	 * @param {string} sender - Predefined channel sender defined for the channel provider.
	 * @param {string} senderName - Default channel sender name for the sender used for email channels. Can be overridden 
	 * by user.
	 */
	constructor(sender, senderName) {
		this.sender = sender;
		this.senderName = senderName;
	}
}


export class DynamicScheduleDataObject {
	/**
	 * @param {string} campaignId
	 * @param {string|null} [id=null]
	 * @param {string} [time='']
	 * @param {string} [timeZoneId='']
	 * @param {ScheduleIntervalType|''} [intervalType='']
	 * @param {string[]} [days=[]] - Days when message definition should be generated, depending on the interval type:
	 * 	- DAY_OF_WEEK: list of integer values between 1 and 7 (including 1 and 7 as well).
	 * 	- DAY_OF_MONTH: list of integer values between 1 and 31 (including 1 and 31).
	 * 	- DATE_OF_YEAR: list of string values in format "MM-dd" (i.e. for the 1st of March the value will be "03-01"). 
	 */
	constructor(campaignId, id = null, time = '', timeZoneId = '', intervalType = '', days = []) {
		this.id = id;
		this.campaignId = campaignId;
		this.time = time;
		this.timeZoneId = timeZoneId;
		this.intervalType = intervalType;
		this.days = days;
	}
}

export class DynamicRecipientsDataObject {
	/**
	 * @param {string} templateId
	 * @param {?string} [id=null]
	 * @param {ConditionListItemDataObject[]} [conditions=[]]
	 * @param {string[]} [groups=[]]
	 */
	constructor(templateId, id = null, conditions = [], groups = []) {
		this.templateId = templateId;
		this.id = id;
		this.conditions = conditions;
		this.groups = groups;
	}
}